import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { FileText, Download, Home, Calendar, User, Mail, Phone, MapPin, LogOut, Edit, Save, X, AlertTriangle, MessageSquare, Receipt } from "lucide-react";

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
const fmtMoney = (v) => v != null ? `${parseFloat(v).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €` : "—";

export default function TenantDashboard() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [leases, setLeases] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ phone: "", address: "", postal_code: "", city: "", password: "" });

  useEffect(() => {
    if (user === false) navigate("/locataire/login");
    if (user && user.role !== "tenant") navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const r = await api.get("/tenant/me");
        if (cancelled) return;
        setMe(r.data);
        setForm({ phone: r.data.phone || "", address: r.data.address || "", postal_code: r.data.postal_code || "", city: r.data.city || "", password: "" });
      } catch (err) { console.error("Failed to load /tenant/me:", err); }
      try {
        const r2 = await api.get("/tenant/leases");
        if (!cancelled) setLeases(r2.data);
      } catch (err) { console.error("Failed to load /tenant/leases:", err); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const downloadPdf = async (leaseId) => {
    try {
      const res = await api.get(`/tenant/leases/${leaseId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `bail-${leaseId.slice(0,8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Bail téléchargé");
    } catch { toast.error("Échec du téléchargement"); }
  };

  const saveProfile = async () => {
    try {
      const payload = { phone: form.phone, address: form.address, postal_code: form.postal_code, city: form.city };
      if (form.password) payload.password = form.password;
      await api.put("/tenant/me", payload);
      toast.success("Profil mis à jour");
      setEditMode(false);
      setForm({ ...form, password: "" });
      const r = await api.get("/tenant/me"); setMe(r.data);
    } catch { toast.error("Échec de la mise à jour"); }
  };

  const doLogout = async () => { await logout(); navigate("/locataire/login"); };

  if (!me) return <div className="p-24 text-center font-mono text-sm">Chargement...</div>;

  const activeLease = leases.find(L => L.status === "active") || leases[0];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-2">— Espace locataire</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Bonjour {me.name?.split(" ")[0]}</h1>
            <p className="text-sm text-slate-500 mt-1">{me.email}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate("/locataire/suivi")} data-testid="tenant-hub-btn"
              className="btn-brutal btn-brutal-solar px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
              <Receipt className="w-4 h-4"/> Suivi & gestion
            </button>
            <button onClick={doLogout} data-testid="tenant-logout"
              className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>

        {/* Active lease summary */}
        {activeLease && (
          <div className="bg-slate-900 text-white p-8 md:p-10 mb-8" data-testid="tenant-active-lease">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-3">— Votre bail {activeLease.status === "active" ? "actif" : "(brouillon)"}</div>
                <h2 className="font-display font-black text-3xl tracking-tight mb-3">{activeLease.property?.title}</h2>
                <p className="text-slate-300 text-sm mb-6 flex items-center gap-2"><MapPin className="w-4 h-4" /> {activeLease.property?.address}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Stat label="Début" value={fmtDate(activeLease.start_date)} />
                  <Stat label="Fin" value={activeLease.end_date ? fmtDate(activeLease.end_date) : "Indéterminée"} />
                  <Stat label="Loyer HT" value={fmtMoney(activeLease.rent_monthly)} />
                  <Stat label="Charges" value={fmtMoney(activeLease.charges_monthly)} />
                  <Stat label="Dépôt de garantie" value={fmtMoney(activeLease.deposit)} />
                  <Stat label="Surface" value={activeLease.surface ? `${activeLease.surface} m²` : "—"} />
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end justify-center gap-3">
                <div className="bg-[#FFC107] text-slate-900 p-6 w-full max-w-xs">
                  <div className="text-xs font-bold uppercase tracking-widest mb-1">Total mensuel</div>
                  <div className="font-display font-black text-4xl">{fmtMoney((activeLease.rent_monthly || 0) + (activeLease.charges_monthly || 0))}</div>
                  <div className="text-xs mt-1">HT — hors charges variables</div>
                </div>
                <button onClick={() => downloadPdf(activeLease.id)} data-testid="td-download-lease"
                  className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                  <Download className="w-4 h-4" /> Télécharger le bail
                </button>
              </div>
            </div>
          </div>
        )}

        {!activeLease && (
          <div className="bg-white border border-slate-900/15 p-8 text-center text-sm text-slate-500 mb-8">
            Aucun bail n'est encore associé à votre compte. Contactez-nous à <a href="mailto:contact@immobiliersolaire.fr" className="underline">contact@immobiliersolaire.fr</a>.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Documents */}
          <div className="bg-white border border-slate-900/15 p-6" data-testid="tenant-documents">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#FFC107]"/> Mes documents</h3>
            </div>
            <div className="space-y-2">
              {leases.map(L => (
                <button key={L.id} onClick={() => downloadPdf(L.id)} data-testid={`td-doc-lease-${L.id}`}
                  className="w-full text-left flex items-center justify-between border border-slate-900/15 p-3 text-sm hover:bg-[#FFC107] transition-colors">
                  <span className="flex items-center gap-2 truncate"><FileText className="w-4 h-4 shrink-0"/>Bail — {L.property?.title || "Local"} ({L.status})</span>
                  <Download className="w-4 h-4 opacity-60"/>
                </button>
              ))}
              {leases.length === 0 && <p className="text-sm text-slate-500">Aucun document pour l'instant.</p>}
            </div>
            <p className="text-xs text-slate-500 mt-4">Les quittances et autres documents seront ajoutés ici (Phase 7.2).</p>
          </div>

          {/* Profile */}
          <div className="bg-white border border-slate-900/15 p-6" data-testid="tenant-profile">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><User className="w-5 h-5 text-[#FFC107]"/> Mon profil</h3>
              {!editMode ? (
                <button onClick={() => setEditMode(true)} data-testid="td-edit-profile" className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:underline"><Edit className="w-3 h-3"/> Modifier</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setEditMode(false)} className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 text-red-600"><X className="w-3 h-3"/> Annuler</button>
                  <button onClick={saveProfile} data-testid="td-save-profile" className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 bg-[#FFC107] px-2 py-1"><Save className="w-3 h-3"/> Enregistrer</button>
                </div>
              )}
            </div>
            <div className="space-y-3 text-sm">
              <Row icon={Mail} label="Email" value={me.email} />
              {editMode ? (
                <>
                  <EditField label="Téléphone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
                  <EditField label="Adresse" value={form.address} onChange={v => setForm({ ...form, address: v })} />
                  <div className="grid grid-cols-[1fr_2fr] gap-2">
                    <EditField label="Code postal" value={form.postal_code} onChange={v => setForm({ ...form, postal_code: v })} />
                    <EditField label="Ville" value={form.city} onChange={v => setForm({ ...form, city: v })} />
                  </div>
                  <EditField label="Nouveau mot de passe (optionnel)" value={form.password} onChange={v => setForm({ ...form, password: v })} type="password" />
                </>
              ) : (
                <>
                  <Row icon={Phone} label="Téléphone" value={me.phone || "—"} />
                  <Row icon={MapPin} label="Adresse" value={[me.address, me.postal_code, me.city].filter(Boolean).join(", ") || "—"} />
                  <Row icon={Home} label="Société" value={me.company || "—"} />
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-[#FFC107] font-bold mb-1">{label}</div>
    <div className="font-mono text-sm">{value}</div>
  </div>
);

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 mt-0.5 opacity-60" />
    <div className="flex-1">
      <div className="text-xs font-bold uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  </div>
);

const EditField = ({ label, value, onChange, type = "text" }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1 block">{label}</span>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} className="input w-full" />
  </label>
);
